import { ApiError, FlexColumn, FlexRow, MaterialIcon, useLoading } from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { ButtonGroup } from 'baseui/button-group';
import { OnChangeParams } from 'baseui/select';
import { LabelSmall } from 'baseui/typography';
import { useEffect, useMemo } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import {
  requestFleetOperationalReportFileByAsset,
  requestFleetOperationalReportFileByGroup,
} from '../../../services/fleetService';
import { apiParamsState } from '../../../storage/app';
import {
  fleetReportEndDateState,
  fleetReportSearchTypeState,
  fleetReportSelectedAssetIdState,
  fleetReportSelectedGroupIdState,
  fleetReportStartDateState,
} from '../../../storage/fleetReports';
import { allGroupsState } from '../../../storage/groups';
import { getEndDate, getEndDateFromDate } from '../../../utils/dates';
import { AssetPicker } from '../../shared/AssetPicker';
import { EnhancedLabeledSelect } from '../../ui/EnhancedLabeledSelect';
import { StyledDateTimeRangePicker } from '../../ui/StyledDateTimeRangePicker';
import { StyledTooltip } from '../../ui/StyledTooltip';
import { useFleetOperational } from '../hooks/useFleetOperational';

function FleetOperationalReportsFilters() {
  const { loading, startLoading, stopLoading } = useLoading();
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const { theme, css } = useStyles();
  const [startDate, setStartDate] = useRecoilState(fleetReportStartDateState);
  const [endDate, setEndDate] = useRecoilState(fleetReportEndDateState);
  const [selectedGroup, setSelectedGroup] = useRecoilState(fleetReportSelectedGroupIdState);
  const [searchType, setSearchType] = useRecoilState(fleetReportSearchTypeState);
  const [selectedAssetId, setSelectedAssetId] = useRecoilState(fleetReportSelectedAssetIdState);
  const { onReload, isUpdating } = useFleetOperational();
  const apiParams = useRecoilValue(apiParamsState);
  const groups = useRecoilValue(allGroupsState);
  const resetSelectedAsset = useResetRecoilState(fleetReportSelectedAssetIdState);
  const resetSelectedGroup = useResetRecoilState(fleetReportSelectedGroupIdState);
  const canDownload = !!startDate && !!endDate && (!!selectedAssetId || !!selectedGroup);

  useEffect(() => {
    resetSelectedAsset();
    resetSelectedGroup();
  }, [searchType]);

  const groupOptions = useMemo(() => {
    if (!groups) return;
    const options: { id: string | undefined; label: string; assetIds?: any }[] = groups.map((group) => ({
      id: group._id,
      label: group.name,
      assetIds: group.assignedAssets,
    }));
    return options;
  }, [groups]);

  const onGroupChange = (params: OnChangeParams) => {
    params.option?.id ? setSelectedGroup(params.option.id as string) : resetSelectedGroup();
  };

  const createReport = async () => {
    if (!apiParams || !startDate || !endDate || (!selectedAssetId && !selectedGroup)) return;
    startLoading();
    const sharedParams = {
      startDate,
      endDate,
      clientId: apiParams.clientId,
      subClientId: apiParams.subClientId,
    };
    let error: ApiError | undefined = undefined;
    if (selectedAssetId) {
      const { error: apiError } = await requestFleetOperationalReportFileByAsset({
        ...sharedParams,
        assetId: selectedAssetId,
      });
      error = apiError;
    }
    if (selectedGroup) {
      const { error: apiError } = await requestFleetOperationalReportFileByGroup({
        ...sharedParams,
        groupId: selectedGroup,
      });
      error = apiError;
    }
    if (!error) {
      showPositiveFeedback('Tu reporte será procesado, visita la sección de descargas para obtenerlo.');
    } else if (error) {
      showFailFeedback(`${error}`);
    }
    stopLoading();
  };

  return (
    <FlexRow classNames={css({ alignItems: 'start' })}>
      <FlexRow
        classNames={css({
          flexWrap: 'wrap',
          flex: 1,
        })}
      >
        <FlexColumn gap={theme.sizing.scale200}>
          <LabelSmall>Buscar por:</LabelSmall>
          <ButtonGroup
            size="compact"
            mode="radio"
            selected={searchType}
            onClick={(event, index) => {
              setSearchType(index);
            }}
          >
            <Button>Vehículos</Button>
            <Button>Grupos</Button>
          </ButtonGroup>
        </FlexColumn>
        {searchType === 0 && (
          <AssetPicker
            onChange={(asset) => setSelectedAssetId(asset._id)}
            onClear={() => setSelectedAssetId(undefined)}
            assetId={selectedAssetId}
            maxWidth={366}
          />
        )}
        {searchType === 1 && (
          <EnhancedLabeledSelect
            label="Selecciona un grupo:"
            options={groupOptions}
            value={selectedGroup ? [{ id: selectedGroup }] : undefined}
            onChange={onGroupChange}
            maxWidth={366}
          />
        )}
        <StyledDateTimeRangePicker
          startLabel="Fecha inicio:"
          endLabel="Fecha fin:"
          dates={[startDate ? new Date(startDate) : undefined, endDate ? new Date(endDate) : undefined]}
          minEndDate={startDate ?? undefined}
          maxEndDate={getEndDate()}
          hideTimes
          todayIsMaxDate={false}
          onDatesChange={(dates) => {
            if (Array.isArray(dates)) {
              if (dates.length > 1 && dates[0] && dates[1]) {
                setStartDate(dates[0].getTime());
                setEndDate(getEndDateFromDate(dates[1]));
              } else if (dates[0]) {
                setStartDate(dates[0].getTime());
                setEndDate(null);
              } else {
                setStartDate(null);
                setEndDate(null);
              }
            }
          }}
        />
      </FlexRow>

      {canDownload && (
        <FlexRow
          centered
          gap={theme.sizing.scale0}
        >
          <StyledTooltip
            content={`Descargar por ${selectedAssetId ? 'activo' : 'grupo'}`}
            showArrow={false}
            placement="top"
          >
            <Button
              size="compact"
              shape="circle"
              kind="tertiary"
              isLoading={loading}
              onClick={createReport}
            >
              <MaterialIcon
                name="download"
                size="default"
              />
            </Button>
          </StyledTooltip>
          <StyledTooltip
            content="Recargar"
            showArrow={false}
            placement="top"
          >
            <Button
              size="compact"
              shape="circle"
              kind="tertiary"
              isLoading={isUpdating}
              onClick={onReload}
            >
              <MaterialIcon
                name="refresh"
                size="default"
              />
            </Button>
          </StyledTooltip>
        </FlexRow>
      )}
    </FlexRow>
  );
}

export default FleetOperationalReportsFilters;
